import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { HowGraded, ReqChips, WorkTable } from "./GradeBreakdown";
import PageFrame, { LoadFailed, Loading } from "./PageFrame";
import { DEFAULT_CRITERIA, fmt, summarize } from "./progress";
import useCoursePlans from "./useCoursePlans";

const courseUrl = (uuid, params = {}) => `/dashboard/course/${uuid}?${new URLSearchParams(params)}`;

/** Grades across every course (prototype gradesPage): a card per course, then all graded work. */
export default function GradesPage() {
  const navigate = useNavigate();
  const { plans, failed, reload } = useCoursePlans();
  const [filter, setFilter] = useState("all");

  const intro = "Attendance, scores and feedback from every course you're taking, and exactly how each counts toward your certificate.";
  if (failed) return <PageFrame title="Grades" intro={intro}><LoadFailed what="grades" onRetry={reload} /></PageFrame>;
  if (!plans) return <PageFrame title="Grades" intro={intro}><Loading /></PageFrame>;
  if (!plans.length) {
    return (
      <PageFrame title="Grades" intro={intro}>
        <section className="complete-box"><div><strong>No grades yet</strong><small>Once you're enrolled in a course, your attendance, quiz scores and feedback appear here.</small></div></section>
        <HowGraded criteria={DEFAULT_CRITERIA} />
      </PageFrame>
    );
  }

  const courses = plans.map((c) => ({ ...c, g: summarize(c.plan) }));
  const key = (c) => `${c.course.uuid}:${c.cohort.id}`;
  const label = (c) => `${c.course.title}${courses.filter((x) => x.course.uuid === c.course.uuid).length > 1 ? ` · ${c.cohort.name}` : ""}`;
  const shown = filter === "all" ? courses : courses.filter((c) => key(c) === filter);

  return (
    <PageFrame title="Grades" intro={intro}>
      {courses.length > 1 && (
        <div className="lb-tabs chips-row" role="group" aria-label="Course">
          {[["all", "All courses"], ...courses.map((c) => [key(c), label(c)])].map(([k, l]) => (
            <button key={k} type="button" aria-pressed={filter === k} onClick={() => setFilter(k)}>{l}</button>
          ))}
        </div>
      )}

      <div className="grade-cards">
        {shown.map((c) => { const { course, cohort, plan, g } = c; return (
          <div key={key(c)} className="grade-card">
            <div className="cc-top"><h4>{label(c)}</h4><button className="link-btn" type="button" onClick={() => navigate(courseUrl(course.uuid, { tab: "grades", cohort_id: cohort.id }))}>Full breakdown ›</button></div>
            <div className="gsum">
              <div><b>{fmt(g.attendance.percent)}</b><span>Attendance · {g.attendance.attended} of {g.attendance.held} classes</span></div>
              <div><b>{g.activities.score ?? "—"}</b><span>Activities score{g.ended ? "" : " so far"}</span></div>
              <div><b>{g.submitted} / {g.workTotal}</b><span>Assignments submitted</span></div>
            </div>
            <div className="req-line">
              <span>Certificate requirements{plan.status === "completed" || g.ready ? " · met" : ""}</span>
              <ReqChips g={g} />
            </div>
          </div>
        ); })}
      </div>

      {shown.map((c) => (
        <section key={key(c)} className="work-block">
          {shown.length > 1 && <h3 className="work-h">{label(c)}</h3>}
          <WorkTable g={c.g} go={(to) => navigate(courseUrl(c.course.uuid, { ...to, cohort_id: c.cohort.id }))} />
        </section>
      ))}

      <HowGraded criteria={courses[0].g.criteria} />
    </PageFrame>
  );
}
